/**
 * Label node for the Plan Actions "Edit plan" entry.
 *
 * The entry keeps its own name in every state; who holds the lock is surfaced as
 * a tooltip instead. A menu that renames itself is hard to scan and impossible
 * to describe in docs, and the holder's email is long enough to truncate inside
 * the 12rem overlay.
 *
 * AntD v4 only makes nested anchors inert inside a disabled dropdown row
 * (`.ant-dropdown-menu-item-disabled a { pointer-events: none }`), so a span
 * still receives hover and the tooltip fires on the disabled entry.
 */
import type { FC } from 'react';
import { Tooltip } from 'antd';

import { TPO_EDIT_PLAN_MENU_LABEL } from '../../../config/testIds';

export interface EditPlanMenuLabelProps {
    label: string;
    /** Whoever holds the lock, or null when it is free or already ours. */
    holder: string | null;
    heldByOtherPrefix: string;
    /** Why the entry is disabled when the lock is free (e.g. operational plan). */
    disabledTooltip?: string | null;
}

const EditPlanMenuLabel: FC<EditPlanMenuLabelProps> = ({
    label,
    holder,
    heldByOtherPrefix,
    disabledTooltip,
}) => {
    if (holder) {
        return (
            <Tooltip title={`${heldByOtherPrefix}${holder}`}>
                <span data-testid={TPO_EDIT_PLAN_MENU_LABEL} data-state-held-by={holder}>
                    {label}
                </span>
            </Tooltip>
        );
    }

    if (disabledTooltip) {
        return (
            <Tooltip title={disabledTooltip}>
                <span>{label}</span>
            </Tooltip>
        );
    }

    return <>{label}</>;
};

EditPlanMenuLabel.displayName = 'EditPlanMenuLabel';
export default EditPlanMenuLabel;
